import '@adornis/bh-institution/client/bh-admin-einrichtungen-list.js';
import type { ProductFilterOptions } from '@adornis/bh-registration/api/institution-offer.js';
import {
  productFilterContext,
  productFilterOptionsContext,
  RegistrationController,
  registrationControllerContext,
} from '@adornis/bh-registration/client/bh-registration-controller.js';
import '@adornis/bh-registration/client/bh-registration-product-filter.js';
import { ProductFilter } from '@adornis/buchungsheld-db/entities/products.js';
import { BuildifyLitElement } from '@adornis/html-based-buildify/client/BuildifyLitElement.js';
import { runInsertNode } from '@adornis/html-based-buildify/client/helper/runInsertNode.js';
import { MenuItemGroup, type INodeConfig } from '@adornis/html-based-buildify/client/types.js';
import { isNodeSelection } from '@adornis/html-based-buildify/client/util.js';
import type { HTMLBase } from '@adornis/html-based-buildify/db/HTMLBase.js';
import { consume } from '@lit/context';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { takeUntil, type Subject } from 'rxjs';

const INSTITUTION_PRODUCT_FILTER_KEY = 'institution-product-filter-key';
export const institutionProductFilterConfig: INodeConfig = {
  node: {
    name: INSTITUTION_PRODUCT_FILTER_KEY,
    schema: {
      draggable: true,
      group: 'block',
      parseDOM: [
        {
          tag: 'node-institution-product-filter',
        },
      ],
      toDOM: node => {
        return ['node-institution-product-filter', { 'no-content': 'true' }];
      },
    },
  },
  menuItems: [
    {
      group: MenuItemGroup.BASIC,
      label: 'Filter',
      icon: 'filter_alt',
      run: view => runInsertNode(INSTITUTION_PRODUCT_FILTER_KEY)(view.state, view),
      shouldVisualize: view => {
        return !isNodeSelection(view.state.selection);
      },
    },
  ],
};

@customElement('node-institution-product-filter')
export class NodeInstitutionProductFilter extends BuildifyLitElement<HTMLBase> {
  @consume({ context: registrationControllerContext })
  @state()
  readonly _registrationController!: RegistrationController;

  @consume({ context: productFilterContext, subscribe: true })
  @state()
  readonly productFilter?: ProductFilter;

  @consume({ context: productFilterOptionsContext, subscribe: true })
  @state()
  readonly productFilterOptions?: ProductFilterOptions;

  override connectedCallback(): void {
    super.connectedCallback();

    const controller = this._registrationController;
    if (!controller) {
      console.warn('no registration controller was consumed');
      return;
    }

    (controller.subject as any as Subject<boolean>)
      .asObservable()
      .pipe(takeUntil(this.disconnected))
      .subscribe(() => {
        this.requestUpdate();
      });
  }

  override content({ content, mode }) {
    if (!this._registrationController || mode === 'edit')
      return html` <bh-registration-product-filter .filter=${new ProductFilter({})}></bh-registration-product-filter> `;

    if (!this.productFilter) {
      console.warn('no filter');
      return html` <bh-registration-product-filter .filter=${new ProductFilter({})}></bh-registration-product-filter> `;
    }

    return html`
      <bh-registration-product-filter
        .filter=${this.productFilter}
        .timeframes=${this.productFilterOptions?.timeframes}
        .productTopics=${this.productFilterOptions?.topics}
        .categories=${this.productFilterOptions?.categories}
      ></bh-registration-product-filter>
    `;
  }

  override styles() {
    return [
      ...super.styles(),
      {
        ':host': {
          overflow: 'visible',
        },
      },
    ];
  }
}
